import { z } from 'zod'

/**
 * IANA time zones, and the offset arithmetic every tenant-local day boundary rests on.
 *
 * There is no date library here and there is not going to be one: `packages/domain`
 * imports nothing but `zod` (`docs/ARCHITECTURE.md` §2). The zone database is reached
 * through `Intl`, which every runtime this code targets ships with full ICU data —
 * workerd, Node 24, and browsers.
 *
 * The rule the whole module serves: **instants are UTC, only day boundaries are
 * tenant-local** (`docs/DECISIONS.md`, "Business timezone — per-tenant"). Nothing here
 * converts an instant into another instant; it converts an instant into the tenant's wall
 * clock so that a *day* can be identified, which is the only thing a zone is needed for.
 */

/** A wall-clock reading in some zone. `month` is 1–12. */
export type ZonedParts = {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  readonly second: number
}

/**
 * Formatters are expensive to construct and immutable once built, so they are memoised
 * per zone. The cache is keyed by the zone name and never holds anything derived from a
 * request, so it is a memo table rather than state: the same arguments always produce the
 * same answer, cached or not.
 */
const formatters = new Map<string, Intl.DateTimeFormat>()

/**
 * A formatter that renders an instant as wall-clock fields in `timeZone`.
 *
 * @throws RangeError if the runtime does not know the zone.
 */
function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timeZone)
  if (cached !== undefined) {
    return cached
  }
  let created: Intl.DateTimeFormat
  try {
    created = new Intl.DateTimeFormat('en-US', {
      timeZone,
      // `hour12: false` yields hour 24 for midnight in some ICU versions; `h23` does not.
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    throw new RangeError(`Unknown time zone: ${JSON.stringify(timeZone)}`)
  }
  formatters.set(timeZone, created)
  return created
}

/**
 * Collects the fields of a formatted instant.
 *
 * Separated from the formatting so that the "a field is missing" path is reachable from a
 * test rather than being an unverifiable defensive branch: a formatter configured without
 * one of the six fields must fail loudly, because silently reading `NaN` here would put a
 * wrong day boundary into an export filter.
 */
export function zonedPartsFromFormatted(formatted: readonly Intl.DateTimeFormatPart[]): ZonedParts {
  const fields = new Map<string, string>()
  for (const part of formatted) {
    fields.set(part.type, part.value)
  }
  const read = (name: string): number => {
    const value = fields.get(name)
    if (value === undefined) {
      throw new RangeError(`The formatted instant has no "${name}" field.`)
    }
    return Number(value)
  }
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

/** The wall clock in `timeZone` at an instant. */
export function zonedPartsAt(instantMs: number, timeZone: string): ZonedParts {
  return zonedPartsFromFormatted(zoneFormatter(timeZone).formatToParts(new Date(instantMs)))
}

/**
 * Reads a wall clock as though it were UTC — the "civil" instant.
 *
 * Not a conversion anyone should perform on real data. It exists because
 * `offset = civil(wallClockInZone(t)) - t` is how an offset is measured without a zone
 * database, and because the inverse — civil minus offset — is how a local midnight is
 * turned back into a UTC instant.
 */
export function civilMs(parts: ZonedParts): number {
  const date = new Date(0)
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day)
  date.setUTCHours(parts.hour, parts.minute, parts.second, 0)
  return date.getTime()
}

/**
 * The zone's offset from UTC at an instant, in milliseconds, positive east of Greenwich.
 *
 * Measured, not looked up, so it is correct for half-hour and three-quarter-hour zones
 * (`Asia/Kolkata` at +05:30, `Asia/Kathmandu` at +05:45) and for historical offsets, with
 * no table of our own to go stale.
 */
export function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const wallClock = civilMs(zonedPartsAt(instantMs, timeZone))
  // The formatter resolves to whole seconds, so compare against a whole-second instant.
  const wholeSeconds = Math.floor(instantMs / 1000) * 1000
  return wallClock - wholeSeconds
}

/** Whether the runtime knows this IANA zone. */
export function isValidTimeZone(value: string): boolean {
  // Offset-only zones ("+05:30") are accepted by Intl and carry no daylight-saving rules,
  // so a tenant configured with one would silently stop observing its own DST. Only named
  // zones are allowed.
  if (/^[+-]/.test(value)) {
    return false
  }
  try {
    zoneFormatter(value)
    return true
  } catch {
    return false
  }
}

/**
 * The canonical spelling of a zone — `europe/berlin` becomes `Europe/Berlin`.
 *
 * Offered separately from the schema rather than folded into it as a transform: a
 * canonicalising schema would publish a request field in the OpenAPI document whose
 * accepted values differ from its stored values, and QA reads that document as the
 * contract.
 *
 * **Not what stores a tenant's zone.** This resolves through `Intl`, so its answer is ICU's
 * and differs between runtimes (an ICU may rewrite `Europe/Kiev` to `Europe/Kyiv`, an alias
 * swap), and it throws on a non-zone. `canonicalTimeZoneCase` (`time-zone-case.ts`) is the
 * deterministic, case-only function the API and the console use
 * (`docs/DECISIONS.md`, "A tenant's `timeZone` is stored in canonical IANA letter case").
 *
 * @throws RangeError if the zone is unknown.
 */
export function canonicalTimeZone(value: string): string {
  if (!isValidTimeZone(value)) {
    throw new RangeError(`Unknown time zone: ${JSON.stringify(value)}`)
  }
  return zoneFormatter(value).resolvedOptions().timeZone
}

/**
 * A tenant's business time zone.
 *
 * Per `docs/DECISIONS.md` this is a per-tenant setting, platform-admin editable, and it
 * decides what "today" means for the expiry sweep, the assignment calendar and export
 * date ranges.
 */
export const timeZoneSchema = z
  .string()
  .refine(isValidTimeZone, { error: 'Expected a named IANA time zone, for example Europe/Berlin.' })
  .meta({
    id: 'TimeZone',
    description:
      "A named IANA time zone, for example Europe/Berlin. Offset-only values such as '+02:00' are rejected because they carry no daylight-saving rules.",
  })

/** The zone a new tenant gets (`docs/DECISIONS.md`, "Business timezone — per-tenant"). */
export const DEFAULT_TIME_ZONE = 'Europe/Berlin'
